import { useEffect, useState } from "react";
import { DrawerForm } from "../../../components/drawers/DrawerForm";
import Indicator from "../../../components/base/Indicator";
import Icon from "../../../components/base/Icon";
import { useHub } from "../../../core/Global";
import { toastError, toastSuccess } from "../../../components/base/Toast";
import { EnterpriseItem, EnterpriseSettings } from "../models/Enterprise";
import { toDateTime } from "../../plancatalog/components/format";
import TextBox from "../../../components/form/TextBox";
import TextArea from "../../../components/form/TextArea";
import DropDown from "../../../components/form/DropDown";
import ToggleSwitch from "../../../components/form/ToggleSwitch";


const PLACEHOLDER = "[PLACEHOLDER]"

const allowances: [string, string][] = [
    ["", "—"], ["users", "Users at once"], ["messages", "Messages"], ["minutes", "Minutes"], ["numbers", "Phone numbers"]
]


// What every offer is built from: the items with their list prices, volume tiers and costs; the
// approval gates; and the two documents a customer signs. Numbers are edited as text and sent
// as numbers.
export default function EnterpriseSettingsDrawer(props: { onClose: () => void }) {

    const hub = useHub()
    const [s, sSet] = useState<EnterpriseSettings>()

    useEffect(() => {
        hub.Get("/system/enterprise/settings", res => {
            // Each item gets a local id, so its fields keep their own state when another is removed.
            if (res.success) sSet({ ...res.data, items: (res.data.items ?? []).map((it: any) => ({ ...it, _uid: Math.random() })) })
            else toastError(res.message ?? "Couldn't load the settings.")
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const set = <K extends keyof EnterpriseSettings>(k: K, v: EnterpriseSettings[K]) => sSet(x => x ? { ...x, [k]: v } : x)
    const setItem = (i: number, patch: Partial<EnterpriseItem>) =>
        sSet(x => x ? { ...x, items: x.items.map((it, j) => j === i ? { ...it, ...patch } : it) } : x)

    const n = (v: string) => {
        const p = parseFloat(v)
        return isNaN(p) ? 0 : p
    }

    const save = (callback: any) => {
        hub.Post("/system/enterprise/settings", s, res => {
            callback(res)
            if (res.success) toastSuccess("Enterprise settings saved.")
            else toastError(res.message ?? "Couldn't save the settings.")
        }, () => callback({ success: false }))
    }

    return (
        <DrawerForm id="drawer-enterprise-settings"
            title="Enterprise settings"
            titleIcon="sliders"
            subtitle={s?.modifiedAt ? "Last saved " + toDateTime(s.modifiedAt) : "Never saved — these are starting figures"}
            size="xlarge"
            buttonText="Save settings"
            confirmDisabled={!s}
            onClose={props.onClose}
            onSubmit={(body, callback) => save(callback)}>

            {
                !s ?
                    <div className="py-20"><Indicator /></div>
                    :
                    <div className="ent-detail">

                        <div className="ent-callout is-warn">
                            <Icon icon="triangle-exclamation" type="solid" size={13} />
                            <div>
                                The costs and tiers start as rough figures. Replace them with Rinvox's real costs: the margin, the
                                below-cost block and the approval gates are all worked out from them.
                            </div>
                        </div>

                        <div className="ent-section">
                            <div className="ent-section-title">Items an offer is built from</div>
                            {
                                s.items.map((it, i) =>
                                    <div key={(it as any)._uid ?? i} className="ent-item">
                                        <div className="ent-grid is-4">
                                            <Text label="Name" value={it.label} onChange={v => setItem(i, { label: v })} />
                                            <Text label="Key" value={it.key} onChange={v => setItem(i, { key: v.toLowerCase().replace(/[^a-z0-9]/g, "") })} />
                                            <Text label="Unit" value={it.unit} onChange={v => setItem(i, { unit: v })} />
                                            <DropDown id={"ent-item-allowance-" + i} name={"allowance" + i} label="Counts as" labelUp block marginBottom={0} classicSelect deselect
                                                source={allowances.filter(([k]) => k).map(([k, v]) => ({ id: k, name: v }))}
                                                defaultValue={it.allowance ?? ""}
                                                onSelect={v => setItem(i, { allowance: v || undefined })}
                                                onClear={() => setItem(i, { allowance: undefined })} />
                                            <Num label="List price" value={it.listPrice} onChange={v => setItem(i, { listPrice: n(v) })} />
                                            <Num label="Cost to Rinvox" value={it.unitCost} onChange={v => setItem(i, { unitCost: n(v) })} />
                                            <div className="d-flex align-items-end pb-2">
                                                <ToggleSwitch raw label="Charged once" checked={!!it.oneTime} onChange={v => setItem(i, { oneTime: v })} />
                                            </div>
                                            <div className="d-flex align-items-end justify-content-end">
                                                <button type="button" className="btn btn-sm btn-light-danger"
                                                    onClick={e => { e.preventDefault(); set("items", s.items.filter((_, j) => j !== i)) }}>
                                                    <Icon icon="trash" size={11} className="me-1" />Remove
                                                </button>
                                            </div>
                                        </div>
                                        <div className="ent-tiers">
                                            <span className="text-gray-600 fs-8 fw-semibold">Volume tiers</span>
                                            {
                                                it.tiers.map((t, k) =>
                                                    <span key={`${k}-${it.tiers.length}`} className="ent-tier">
                                                        from
                                                        <TierNum value={t.from}
                                                            onChange={v => setItem(i, { tiers: it.tiers.map((x, j) => j === k ? { ...x, from: n(v) } : x) })} />
                                                        at $
                                                        <TierNum value={t.unitPrice}
                                                            onChange={v => setItem(i, { tiers: it.tiers.map((x, j) => j === k ? { ...x, unitPrice: n(v) } : x) })} />
                                                        <button type="button" onClick={e => { e.preventDefault(); setItem(i, { tiers: it.tiers.filter((_, j) => j !== k) }) }}>
                                                            <Icon icon="xmark" size={9} />
                                                        </button>
                                                    </span>
                                                )
                                            }
                                            <button type="button" className="btn btn-sm btn-light py-1"
                                                onClick={e => { e.preventDefault(); setItem(i, { tiers: [...it.tiers, { from: 1, unitPrice: it.listPrice }] }) }}>
                                                <Icon icon="plus" size={9} className="me-1" />tier
                                            </button>
                                        </div>
                                    </div>
                                )
                            }
                            <button type="button" className="btn btn-sm btn-light-primary mt-3"
                                onClick={e => { e.preventDefault(); set("items", [...s.items, { key: "item" + (s.items.length + 1), label: "New item", unit: "month", listPrice: 0, unitCost: 0, tiers: [], _uid: Math.random() } as any]) }}>
                                <Icon icon="plus" size={10} className="me-1" />Add an item
                            </button>
                        </div>

                        <div className="ent-section">
                            <div className="ent-section-title">Costs and approval</div>
                            <div className="ent-grid is-3">
                                <Num label="Cost of a message" value={s.messageCost} onChange={v => set("messageCost", n(v))} />
                                <Num label="Cost of a call minute" value={s.minuteCost} onChange={v => set("minuteCost", n(v))} />
                                <Num label="Offer open for (days)" value={s.defaultValidDays} onChange={v => set("defaultValidDays", Math.round(n(v)))} />
                                <Num label="No approval up to (% off)" value={s.selfApproveMaxDiscount} onChange={v => set("selfApproveMaxDiscount", n(v))} />
                                <Num label="Sales manager up to (% off)" value={s.managerApproveMaxDiscount} onChange={v => set("managerApproveMaxDiscount", n(v))} />
                                <Num label="Director below margin (%)" value={s.directorBelowMargin} onChange={v => set("directorBelowMargin", n(v))} />
                            </div>
                            <div className="ent-note">An overage rate under the message or minute cost is refused on every offer.</div>
                        </div>

                        <div className="ent-section">
                            <div className="ent-section-title">Documents the customer signs</div>
                            <Doc label="Service Agreement" value={s.serviceAgreementText} onChange={v => set("serviceAgreementText", v)} />
                            <Doc label="Data Processing Agreement" value={s.dpaText} onChange={v => set("dpaText", v)} />
                            <div className="ent-note">
                                The Order Form is written from each offer. Every signature records a fingerprint of the exact wording signed, so
                                changing these later never changes what an earlier customer agreed to.
                            </div>
                        </div>
                    </div>
            }
        </DrawerForm>
    )
}


// The project's own inputs, so this form looks like every other form in the console.
const fieldName = (label: string) => label.toLowerCase().replace(/[^a-z0-9]+/g, "-")

function Text(props: { label: string, value?: string, onChange: (v: string) => void }) {
    return (
        <TextBox name={fieldName(props.label)}
            label={props.label} labelUp block noMarginBottom
            value={props.value ?? ""} onTextChanges={props.onChange} />
    )
}

function Num(props: { label: string, value: number, onChange: (v: string) => void }) {
    const [text, textSet] = useState<string>(String(props.value ?? 0))
    return (
        <TextBox name={fieldName(props.label)}
            label={props.label} labelUp block noMarginBottom decimal
            value={text} onTextChanges={v => { textSet(v); props.onChange(v) }} />
    )
}

// Held as typed, so "0.01" can be typed through "0." and "0.0".
function TierNum(props: { value: number, onChange: (v: string) => void }) {
    const [text, textSet] = useState<string>(String(props.value ?? 0))
    return (
        <input value={text} inputMode="decimal"
            onChange={e => {
                if (!/^\d*\.?\d*$/.test(e.target.value)) return
                textSet(e.target.value)
                props.onChange(e.target.value)
            }} />
    )
}

function Doc(props: { label: string, value?: string, onChange: (v: string) => void }) {
    const placeholder = !props.value?.trim() || props.value.includes(PLACEHOLDER)
    return (
        <div className="mb-5">
            {placeholder && <span className="badge badge-light-danger mb-2">Not written yet — offers cannot be sent</span>}
            <TextArea id={"ent-doc-" + fieldName(props.label)} name={fieldName(props.label)} label={props.label}
                labelUp block noMarginBottom rows={8}
                value={props.value ?? ""} onChange={e => props.onChange(e.target.value)} />
        </div>
    )
}
